import React, { useEffect, useState } from 'react';
import {
  Plug, RefreshCw, Loader2, CheckCircle2, Webhook, Link2, Link2Off,
  Hammer, Calculator, MessageSquare, ArrowUpRight,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader } from '@/components/common/Feedback';
import api from '@/lib/api';
import { fmtRelative } from '@/lib/format';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const CAT_ICON = { 'Project Management': Hammer, 'Accounting': Calculator, 'Communication': MessageSquare };

export default function Integrations() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = () => api.get('/integrations').then((r) => setData(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const act = async (provider, action) => {
    setBusy(`${provider}:${action}`);
    const tId = action === 'sync' ? toast.loading('Syncing — running AI detection on incoming items…') : undefined;
    try {
      if (action === 'sync') {
        const r = await api.post(`/integrations/${provider}/sync`);
        toast.success(r.data.message || 'Synced', { id: tId });
      } else {
        await api.post(`/integrations/${provider}/${action}`, action === 'connect' ? {} : undefined);
        toast.success(action === 'connect' ? 'Connected (sandbox)' : 'Disconnected');
      }
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed', { id: tId }); }
    finally { setBusy(null); }
  };

  if (loading) return <Loader label="Loading integrations..." />;

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 flex items-start gap-3">
        <Webhook className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
        <div className="text-sm text-blue-900">
          <span className="font-semibold">Sandbox mode.</span> Connectors run on realistic sample data through the same
          normalize &rarr; store &rarr; detect pipeline as live. Add OAuth credentials to flip any provider to live &mdash; no code changes.
          Inbound webhooks are active at <span className="font-mono text-xs">/api/webhooks/{`{email,whatsapp,procore,autodesk}`}</span>.
        </div>
      </div>

      {data.categories.map((cat) => {
        const items = data.integrations.filter((i) => i.category === cat);
        const CatIcon = CAT_ICON[cat] || Plug;
        return (
          <div key={cat}>
            <div className="flex items-center gap-2 mb-3">
              <CatIcon className="h-4 w-4 text-slate-500" />
              <h3 className="text-sm font-semibold text-slate-700">{cat}</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {items.map((i) => {
                const connected = i.status === 'connected';
                return (
                  <Card key={i.provider} className="p-5 flex flex-col" data-testid={`integration-${i.provider}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-sm font-semibold text-slate-800">{i.name}</div>
                        <div className="flex items-center gap-1.5 mt-1">
                          <span className={cn('inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold',
                            connected ? 'bg-green-50 text-money-700 border border-green-200' : 'bg-slate-100 text-slate-500 border border-slate-200')}>
                            {connected ? <CheckCircle2 className="h-3 w-3" /> : <Link2Off className="h-3 w-3" />}
                            {connected ? 'Connected' : 'Disconnected'}
                          </span>
                          {connected && <Badge variant="secondary" className="text-[10px]">Sandbox</Badge>}
                        </div>
                      </div>
                      {i.supports_webhook && <span title="Webhook supported"><Webhook className="h-4 w-4 text-slate-300" /></span>}
                    </div>

                    <p className="text-xs text-slate-500 mt-3 flex-1">{i.description}</p>

                    <div className="flex flex-wrap gap-1 mt-3">
                      {(i.capabilities || []).slice(0, 4).map((c) => (
                        <span key={c} className="text-[10px] rounded bg-slate-100 text-slate-600 px-1.5 py-0.5">{c.replace(/_/g, ' ')}</span>
                      ))}
                    </div>

                    {connected && (
                      <div className="mt-3 text-[11px] text-slate-400">
                        {i.account_label}<br />
                        {i.last_sync_at ? `Last sync ${fmtRelative(i.last_sync_at)} \u00b7 ${i.items_synced} items` : 'Not yet synced'}
                      </div>
                    )}

                    <div className="flex items-center gap-2 mt-4">
                      {connected ? (
                        <>
                          {i.has_inbound_feed ? (
                            <Button size="sm" className="bg-primary hover:bg-ink-700 h-8 flex-1"
                              disabled={busy === `${i.provider}:sync`} onClick={() => act(i.provider, 'sync')}
                              data-testid={`sync-${i.provider}`}>
                              {busy === `${i.provider}:sync` ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
                              Sync now
                            </Button>
                          ) : (
                            <Button size="sm" variant="secondary" className="h-8 flex-1 border border-slate-300"
                              disabled={busy === `${i.provider}:sync`} onClick={() => act(i.provider, 'sync')}
                              data-testid={`sync-${i.provider}`}>
                              {busy === `${i.provider}:sync` ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <ArrowUpRight className="h-3.5 w-3.5 mr-1" />}
                              Push sync
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" className="h-8 text-red-600"
                            disabled={busy === `${i.provider}:disconnect`} onClick={() => act(i.provider, 'disconnect')}
                            data-testid={`disconnect-${i.provider}`}>
                            Disconnect
                          </Button>
                        </>
                      ) : (
                        <Button size="sm" className="bg-money hover:bg-money-700 h-8 flex-1"
                          disabled={busy === `${i.provider}:connect`} onClick={() => act(i.provider, 'connect')}
                          data-testid={`connect-${i.provider}`}>
                          {busy === `${i.provider}:connect` ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Link2 className="h-3.5 w-3.5 mr-1" />}
                          Connect
                        </Button>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
